'use client'

import * as React from 'react'
import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAppreciationCount, incrementAppreciation } from '@/lib/firebase'
import { useAuth } from '@/components/auth'

export function ThankYouButton() {
  const { user } = useAuth()
  const [count, setCount] = React.useState<number | null>(null)
  const [hasAppreciated, setHasAppreciated] = React.useState(false)
  const [isAnimating, setIsAnimating] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  // Check localStorage and fetch count on mount
  React.useEffect(() => {
    const appreciated = localStorage.getItem('hasAppreciated') === 'true'
    setHasAppreciated(appreciated)
    
    // Fetch current count
    getAppreciationCount().then(setCount).catch(() => setCount(0))
  }, [])

  const handleClick = async () => {
    if (!user || hasAppreciated || isLoading) return
    
    setIsLoading(true)
    setIsAnimating(true)
    
    try {
      const newCount = await incrementAppreciation()
      setCount(newCount)
      setHasAppreciated(true)
      localStorage.setItem('hasAppreciated', 'true')
    } catch (error) {
      console.error('Failed to save appreciation:', error)
    } finally {
      setIsLoading(false)
      setTimeout(() => setIsAnimating(false), 600)
    }
  }
  
  const canClick = user && !hasAppreciated

  const getTitle = () => {
    if (hasAppreciated) return 'Thanks for your support!'
    if (!user) return 'Sign in to say thanks'
    return 'Click to say thanks!'
  }

  return (
    <button
      onClick={handleClick}
      disabled={!canClick || isLoading}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
        'border',
        hasAppreciated
          ? 'bg-pink-50 dark:bg-pink-950/30 border-pink-200 dark:border-pink-800 text-pink-600 dark:text-pink-400 cursor-default'
          : canClick
            ? 'bg-card border-border hover:bg-pink-50 dark:hover:bg-pink-950/30 hover:border-pink-300 dark:hover:border-pink-700 hover:text-pink-600 dark:hover:text-pink-400 cursor-pointer'
            : 'bg-card border-border text-muted-foreground cursor-default opacity-70'
      )}
      title={getTitle()}
    >
      <Heart
        className={cn(
          'h-4 w-4 transition-all',
          hasAppreciated && 'fill-pink-500 text-pink-500',
          isAnimating && 'scale-125'
        )}
      />
      <span>
        {count !== null ? (
          <>
            <span className="font-semibold">{count}</span>
            <span className="text-muted-foreground ml-1">thanks</span>
          </>
        ) : (
          <span className="text-muted-foreground">...</span>
        )}
      </span>
    </button>
  )
}

